import { defineStore } from 'pinia';
import { decodeScenario, encodeScenario } from '../lib/encode';
import { defaultScenario } from '../lib/defaults';
import { ScenarioInputs } from '../lib/types';

const STORAGE_KEY = 'zoo-scenario';

function deepMerge<T extends Record<string, any>>(base: T, override?: Partial<T>): T {
  if (!override || typeof override !== 'object') {
    return base;
  }
  const result: Record<string, any> = Array.isArray(base) ? [...(base as any[])] : { ...base };
  for (const key of Object.keys(override)) {
    const overrideVal = (override as Record<string, any>)[key];
    if (overrideVal === undefined) continue;
    const baseVal = (base as Record<string, any>)[key];
    if (
      overrideVal &&
      typeof overrideVal === 'object' &&
      !Array.isArray(overrideVal) &&
      baseVal &&
      typeof baseVal === 'object' &&
      !Array.isArray(baseVal)
    ) {
      result[key] = deepMerge(baseVal, overrideVal);
    } else {
      result[key] = overrideVal;
    }
  }
  return result as T;
}

function hydrateScenario(source: ScenarioInputs) {
  return deepMerge(defaultScenario, source);
}

function loadFromStorage(): ScenarioInputs {
  const hash = window.location.hash.replace('#', '');
  if (hash) {
    const decoded = decodeScenario<ScenarioInputs>(hash);
    if (decoded) {
      return hydrateScenario(decoded);
    }
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as ScenarioInputs;
      return hydrateScenario(parsed);
    } catch (err) {
      console.warn('Failed to parse scenario from storage', err);
    }
  }
  return defaultScenario;
}

export const useScenarioStore = defineStore('scenario', {
  state: () => ({
    scenario: loadFromStorage()
  }),
  actions: {
    update<K extends keyof ScenarioInputs>(key: K, value: ScenarioInputs[K]) {
      this.scenario = { ...this.scenario, [key]: value };
      this.persist();
    },
    setScenario(next: ScenarioInputs) {
      this.scenario = hydrateScenario(next);
      this.persist();
    },
    persist() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.scenario));
      const encoded = encodeScenario(this.scenario);
      window.location.hash = encoded;
    },
    reset() {
      this.scenario = defaultScenario;
      this.persist();
    }
  }
});
